import { defaultPagination } from '@Core';
import { IGetCameraPhotoProps } from '@EcamModel/controllers/ICameraPhotoHttpController';
import {
    CameraPhotos,
    CameraWithInfo,
    CaptureType,
    ContraventionReasons,
    GeoZoneCoordinates,
    GeoZones,
} from '@EcamModel/model';
import StyledDateRangePicker from '@components/StyledDateRangePicker';
import FilterRoot from '@components/filter/FilterRoot';
import IOSSwitch from '@components/IOSSwitch';
import StyledAutocomplete from '@components/styled-autocomplete';
import useStyledAutocomplete from '@components/styled-autocomplete/useStyledAutocomplete';
import { cameraPhotoController, cameraViewController } from '@controllers/index';
import { appConfig } from '@configs/index';
import { SelectionItem } from '@models/cameras/SelectionItem';
import {
    Box,
    Dialog,
    FormControl,
    FormControlLabel,
    Grid,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';
import { search } from '@pages/cameras/list/helpers';
import { TransformWrapperComponent } from '@pages/verification/pc-details/components/EvidencePhotoGroup';
import color from '@theme/Colors';
import _ from 'lodash';
import moment, { Moment } from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getSecond } from 'src/helpers';
import BaseItemCard from './BaseItemCard';
import BaseList from './BaseList';
import { useImageDimensions } from './PreviewImage';
import { useLocation } from 'react-router-dom';
import IMultiSelectWithCheckBox from '@components/multi-select/IMultiSelectWithCheckBox';
import { CameraView } from '@EcamModel/model/CameraView';

export type FilterType = {
    startDate: string;
    endDate: string;
    plate?: string;
};

type Props = {
    camera: CameraWithInfo;
    isCheckScrollPopup?: boolean;
    isCheckSelectedItemPopup?: boolean;
    selectedItemsLivePhotos?: CameraPhotos | null;
    onSelectedItemLivePhoto?: (item: CameraPhotos | null) => void;
    defaultTime?: boolean;
    defaultFilter?: FilterType;
};

export type Filter = {
    page: number;
    pageSize: number;
    timeFrom: Date;
    timeTo: Date;
    capType?: SelectionItem;
    geoZone?: GeoZones;
    reason?: ContraventionReasons;
    dayStartTime: moment.Moment | null;
    dayEndTime: moment.Moment | null;
    plate?: string;
    hasRecognizedPlate?: boolean;
    cameraViews?: CameraView[];
};

export default function LivePhotos(props: Props) {
    const [paging, setPaging] = useState(defaultPagination<CameraPhotos>());
    const [loading, setLoading] = useState(true);
    const [cameraViews, setCameraViews] = useState<CameraView[]>([]);

    const {
        camera,
        isCheckScrollPopup = false,
        isCheckSelectedItemPopup = true,
        selectedItemsLivePhotos,
        onSelectedItemLivePhoto,
        defaultTime,
        defaultFilter,
    } = props;

    const { search: searchParams } = useLocation();
    const queryParams = new URLSearchParams(searchParams);
    const plate = decodeURIComponent(queryParams.get('plate') || '') || defaultFilter?.plate;
    const startDate = decodeURIComponent(queryParams.get('startDate') || '') || defaultFilter?.startDate;
    const endDate = decodeURIComponent(queryParams.get('endDate') || '') || defaultFilter?.endDate;

    const initFilter: Filter = useMemo(() => {
        const timeZone = camera.TimeZone || appConfig.timeZone;
        const now = moment().tz(timeZone);
        const referenceDay = now.clone().subtract(defaultTime && now.hour() < 10 ? 1 : 0, 'day');

        const getMomentOrDefault = (date: string | null, isStart: boolean) =>
            date
                ? moment(date)
                      .tz(timeZone)
                      .startOf(isStart ? 'day' : 'minute')
                : referenceDay.clone().startOf('day');

        return {
            page: 1,
            pageSize: 20,
            timeFrom: getMomentOrDefault(defaultTime ? null : startDate!, true).toDate(),
            timeTo: getMomentOrDefault(defaultTime ? null : endDate!, false)
                .endOf('day')
                .toDate(),
            dayStartTime: defaultTime
                ? referenceDay.clone().startOf('day').hour(10).startOf('minute')
                : startDate
                ? moment(startDate).tz(timeZone).startOf('minute')
                : null,
            dayEndTime: defaultTime
                ? referenceDay.clone().startOf('day').hour(16).startOf('minute')
                : endDate
                ? moment(endDate).tz(timeZone).add(1, 'minute').startOf('minute')
                : null,
            plate: plate ?? '',
            hasRecognizedPlate: false,
            cameraViews: [],
            refetch: false,
        };
    }, [camera.TimeZone, startDate, endDate, defaultFilter?.plate, camera.Id]);
    const [filter, setFilter] = useState<Filter>(initFilter);

    const [focusedItem, setFocusedItem] = useState<CameraPhotos | undefined>();
    const [scaleZoomChangeCursor, setScaleZoomChangeCursor] = useState<number>(1);

    const { image, isError } = useImageDimensions(`${focusedItem?.BlobName}`);

    const scaleFull = 1280 / (image?.width ?? 1920);

    const prevGeoZonePoints: GeoZoneCoordinates[][] = camera.GeoZones?.map((gz) => {
        return gz.GeoZoneCoordinates?.sort((a, b) => a.Index - b.Index).concat(gz.GeoZoneCoordinates[0]);
    }) as GeoZoneCoordinates[][];

    const prevGeoZonePointsCameraView: GeoZoneCoordinates[][] = focusedItem?.CameraView?.GeoZones?.map((gz) => {
        return gz.GeoZoneCoordinates?.sort((a, b) => a.Index - b.Index).concat(gz.GeoZoneCoordinates[0]);
    }) as GeoZoneCoordinates[][];

    const vehicleCoordinates = (focusedItem?.CameraPhotoVehicles || []).map((c) => {
        return [
            { XPoint: c.X1, YPoint: c.Y1 },
            { XPoint: c.X2, YPoint: c.Y2 },
            { XPoint: c.X3, YPoint: c.Y3 },
            { XPoint: c.X4, YPoint: c.Y4 },
            { XPoint: c.X1, YPoint: c.Y1 },
        ] as GeoZoneCoordinates[];
    });

    const geoZones = prevGeoZonePoints?.concat(vehicleCoordinates).map((polygon) =>
        polygon.map((point) => ({
            x: point.XPoint * scaleFull,
            y: point.YPoint * scaleFull,
            geoZoneId: point.GeoZoneId,
        }))
    );

    const geoZonesCameraView = prevGeoZonePointsCameraView?.map((polygon) =>
        polygon.map((point) => ({
            x: point.XPoint * scaleFull,
            y: point.YPoint * scaleFull,
            geoZoneId: point.GeoZoneId,
        }))
    );

    const handleChangeSearch = useCallback(
        _.debounce((text: string) => {
            setFilter((prev) => ({ ...prev, plate: text, page: 1 }));
        }, 300),
        []
    );

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            const _filter: IGetCameraPhotoProps = {
                page: filter.page,
                pageSize: filter.pageSize,
                sorts: ['-CaptureAt'],
                CameraId: camera.Id!,
                FromDate: filter.timeFrom,
                ToDate: filter.timeTo,
                ContraventionReasonId: filter.reason?.Id,
                GeoZoneId: filter.geoZone?.Id,
                FromTime: filter.dayStartTime ? getSecond(filter.dayStartTime) : undefined,
                ToTime: filter.dayEndTime ? getSecond(filter.dayEndTime) : undefined,
                filter: {
                    CaptureType: filter.capType?.id,
                    ViewId:
                        filter.cameraViews && filter.cameraViews?.length > 0
                            ? filter.cameraViews?.map((view) => view.Id)
                            : undefined,
                },
                Plate: filter.plate,
                HasRecognizedPlate: filter.hasRecognizedPlate || undefined,
            };

            if (!_filter.Plate) delete _filter.Plate;

            cameraPhotoController
                .getLivePhotos(_filter)
                .then((res) => {
                    setPaging(res);
                })
                .finally(() => {
                    setLoading(false);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                });
        };
        init();
    }, [
        filter.page,
        filter.timeFrom,
        filter.timeTo,
        camera.Id,
        filter.capType,
        filter.reason?.Id,
        filter.geoZone?.Id,
        filter.dayStartTime,
        filter.dayEndTime,
        filter.pageSize,
        filter.plate,
        filter.hasRecognizedPlate,
        filter.cameraViews,
    ]);

    useEffect(() => {
        const fetchCameraViews = async () => {
            try {
                const response = await cameraViewController.list({
                    page: 1,
                    pageSize: 10000,
                    filter: {
                        CameraId: camera.Id,
                    },
                });
                setCameraViews(response.rows || []);
            } catch (error) {
                console.error('Error fetching camera views:', error);
                setCameraViews([]);
            }
        };

        if (camera.Id) {
            fetchCameraViews();
        }
    }, [camera.Id]);

    const geoZoneAutocomplete = useStyledAutocomplete({
        list: {
            options: camera.GeoZones || [],
            isFiltered: (option, searchText) => search(`Geo-zone ${option.Id}`, searchText),
        },
    });

    const reasonAutocomplete = useStyledAutocomplete({
        list: {
            options:
                _.unionBy(
                    props.camera?.GeoZones?.map((g) => g.ContraventionReason!),
                    (c) => c?.Id
                ) || [],
            isFiltered: (option, searchText) =>
                search(option?.ContraventionReasonTranslations?.[0]?.Summary || '', searchText),
        },
    });

    const capTypeAutocomplete = useStyledAutocomplete({
        list: {
            options: [
                {
                    id: CaptureType.command,
                    value: 'Command',
                },
                {
                    id: CaptureType.trigged,
                    value: 'Trigged',
                },
            ],
            isFiltered: (option, searchText) => search(option.value, searchText),
        },
    });

    function updateFilter(params: Partial<Filter>) {
        setFilter((prev) => ({ ...prev, page: 1, ...params }));
    }

    function handleChangeTime(from: Moment | null, to: Moment | null) {
        let updatedFrom = from;
        let updatedTo = to;

        if (!updatedTo) {
            updatedTo = moment().endOf('day');
        }

        if (updatedFrom && updatedTo && updatedFrom.isAfter(updatedTo)) {
            [updatedFrom, updatedTo] = [updatedTo, updatedFrom];
        }

        updateFilter({ dayStartTime: updatedFrom, dayEndTime: updatedTo });
    }
    const searchRef = useRef<HTMLInputElement | null>(null);

    const handleReset = () => {
        setFilter({
            ...initFilter,
            timeFrom: moment()
                .tz(camera.TimeZone || appConfig.timeZone)
                .startOf('day')
                .toDate(),
            timeTo: moment()
                .tz(camera.TimeZone || appConfig.timeZone)
                .endOf('day')
                .toDate(),
            dayStartTime: null,
            dayEndTime: null,
            plate: undefined,
            hasRecognizedPlate: false,
            cameraViews: [],
        });
        if (searchRef.current) searchRef.current.value = '';
        onSelectedItemLivePhoto?.(null);
    };

    const handleRadioChangeItemsLivePhotos = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedItem = paging.rows?.find((item) => item.Id === Number(event.target.value));
        if (selectedItem) {
            onSelectedItemLivePhoto?.(selectedItem);
        }
    };

    const handleTransform = (e: any) => {
        setScaleZoomChangeCursor(e.instance.transformState.scale);
    };

    return (
        <div style={{ margin: 'auto' }}>
            <FilterRoot
                handleResetFilter={handleReset}
                rightTitleContent={
                    <Stack direction={'row'} spacing={2}>
                        {!_.isNil(camera.PTZCameraId) && (
                            <Stack minWidth={250}>
                                <IMultiSelectWithCheckBox
                                    listData={cameraViews}
                                    label="Camera views"
                                    value={filter.cameraViews || []}
                                    keyLabel="CameraViewName"
                                    keyId="Id"
                                    setFilter={(selectedViews) => {
                                        onSelectedItemLivePhoto?.(null);
                                        updateFilter({ cameraViews: selectedViews });
                                    }}
                                    placeHolder="Select camera views..."
                                    limitTags={2}
                                    chipSize={120}
                                    autoPositionAdornment
                                />
                            </Stack>
                        )}

                        <Stack minWidth={250}>
                            <StyledDateRangePicker
                                value={{
                                    startDate: filter.timeFrom,
                                    endDate: filter.timeTo,
                                }}
                                onChange={(value) => {
                                    console.log(`value:`, value);
                                    setFilter((prev) => ({
                                        ...prev,
                                        timeFrom: value.startDate!,
                                        timeTo: value.endDate!,
                                        page: 1,
                                    }));
                                    onSelectedItemLivePhoto?.(null);
                                }}
                                label={'Date range'}
                                timezone={camera.TimeZone || appConfig.timeZone}
                            />
                        </Stack>

                        <Stack direction={'row'} spacing={2}>
                            {/* <StyledTimePicker /> */}
                            <TimePicker
                                label="From"
                                value={filter.dayStartTime || null}
                                ampm={false}
                                // onAccept={(newValue) => {
                                //     console.log(newValue);
                                //     updateFilter({
                                //         dayStartTime: newValue || undefined,
                                //         dayEndTime: !filter.dayEndTime ? moment().endOf('day') : filter.dayEndTime,
                                //     });
                                // }}

                                onAccept={(newValue) => {
                                    onSelectedItemLivePhoto?.(null);
                                    handleChangeTime(newValue, filter.dayEndTime);
                                }}
                                sx={{
                                    width: 120,
                                    '& .MuiSvgIcon-root': {
                                        width: 18,
                                        height: 18,
                                    },
                                }}
                            />
                            <TimePicker
                                label="To"
                                ampm={false}
                                disabled={!filter.dayStartTime}
                                value={filter.dayEndTime || null}
                                // shouldDisableTime={(value, view) => {
                                //     console.log({ value: value.toDate() });
                                //     return true;
                                // }}
                                onAccept={(newValue) => {
                                    onSelectedItemLivePhoto?.(null);
                                    handleChangeTime(filter.dayStartTime, newValue);
                                }}
                                sx={{
                                    width: 120,
                                    '& .MuiSvgIcon-root': {
                                        width: 18,
                                        height: 18,
                                    },
                                }}
                            />
                        </Stack>
                    </Stack>
                }
                filterChild={
                    <Stack flex={1} direction={'row'} spacing={2}>
                        <Stack flex={1}>
                            <TextField
                                label="Plate"
                                placeholder="Search by plate number..."
                                // value={filter.plate}
                                defaultValue={filter.plate}
                                inputRef={searchRef}
                                inputProps={{}}
                                onChange={(e) => {
                                    onSelectedItemLivePhoto?.(null);
                                    handleChangeSearch(e.target.value);
                                    // setFilter((prev) => ({ ...prev, plate: e.target.value as string }));
                                }}
                            />
                        </Stack>
                        <Stack flex={1}>
                            <StyledAutocomplete
                                {...geoZoneAutocomplete}
                                getOptionLabel={(o) => `Geo-zone ${o.Id}`}
                                label="Geo-zone"
                                value={filter.geoZone}
                                placeholder="All geo-zones"
                                onChange={(c) => {
                                    onSelectedItemLivePhoto?.(null);
                                    updateFilter({ geoZone: c, reason: c?.ContraventionReason });
                                }}
                            />
                        </Stack>
                        <Stack flex={1}>
                            <StyledAutocomplete
                                {...reasonAutocomplete}
                                getOptionLabel={(o) =>
                                    o?.ContraventionReasonTranslations?.[0]?.Summary || `Unknown ${o?.Id}`
                                }
                                label="Contravention"
                                value={filter.reason}
                                placeholder="All contraventions"
                                onChange={(c) => {
                                    onSelectedItemLivePhoto?.(null);
                                    updateFilter({ reason: c });
                                }}
                            />
                        </Stack>
                        {/* <Stack flex={1}>
                        <StyledAutocomplete
                            {...statusAutocomplete}
                            getOptionLabel={(o) => o.value}
                            label="Time range"
                            value={filter.capType}
                            placeholder="All status"
                            onChange={(c) => {
                                setFilter((prev) => ({ ...prev, capType: c, page: 1 }));
                            }}
                        />
                    </Stack> */}
                        <Stack flex={1}>
                            <StyledAutocomplete
                                {...capTypeAutocomplete}
                                getOptionLabel={(o) => o.value}
                                label="Capture type"
                                value={filter.capType}
                                placeholder="All status"
                                onChange={(c) => {
                                    // setFilter((prev) => ({ ...prev, capType: c, page: 1 }));
                                    onSelectedItemLivePhoto?.(null);
                                    updateFilter({ capType: c });
                                }}
                            />
                        </Stack>
                        {/* Added new filter checkbox for recognized plates with smaller stack */}
                        <Stack width={'max-content'} direction="row" alignItems="center">
                            <FormControlLabel
                                control={
                                    <IOSSwitch
                                        iosSwitchProps={{
                                            checked: filter.hasRecognizedPlate,
                                            onChange: (e: any) => {
                                                onSelectedItemLivePhoto?.(null);
                                                updateFilter({ hasRecognizedPlate: e.target.checked });
                                            },
                                        }}
                                    />
                                }
                                label="Has recognized plate"
                                sx={{
                                    marginLeft: 1,
                                }}
                            />
                        </Stack>
                    </Stack>
                }
            />

            <Box mt={2}>
                <Box
                    sx={
                        isCheckScrollPopup
                            ? {
                                  height: '500px',
                                  overflowY: 'auto',
                                  '::-webkit-scrollbar': {
                                      width: '2px',
                                  },
                              }
                            : {}
                    }
                >
                    <BaseList
                        list={paging.rows}
                        loading={loading}
                        renderItem={(photo) => (
                            <>
                                {isCheckScrollPopup && isCheckSelectedItemPopup ? (
                                    <Box
                                        key={photo.Id}
                                        bgcolor={color.white}
                                        p={1}
                                        border={1}
                                        borderColor={color.grey200}
                                        borderRadius={1}
                                        position={'relative'}
                                    >
                                        <FormControl>
                                            <RadioGroup
                                                row
                                                aria-labelledby="radio-buttons-group-label"
                                                name="radio-buttons-group"
                                                value={selectedItemsLivePhotos?.Id || ''}
                                                onChange={handleRadioChangeItemsLivePhotos}
                                            >
                                                <Grid container>
                                                    <Grid item>
                                                        <FormControlLabel
                                                            sx={{ ml: '-6px !important' }}
                                                            control={<Radio value={photo.Id ?? 0} />}
                                                            label={photo.Id ?? 0}
                                                        />
                                                    </Grid>
                                                </Grid>
                                            </RadioGroup>
                                        </FormControl>
                                        <BaseItemCard
                                            timezone={camera.TimeZone}
                                            photo={photo}
                                            onClick={setFocusedItem}
                                        />
                                    </Box>
                                ) : (
                                    <BaseItemCard timezone={camera.TimeZone} photo={photo} onClick={setFocusedItem} />
                                )}
                            </>
                        )}
                        count={paging.total}
                        page={filter.page}
                        pageSize={filter.pageSize}
                        onChangePage={(page, pageSize) => {
                            // handleChangeParams({ page: value });
                            onSelectedItemLivePhoto?.(null);
                            setFilter((prev) => ({ ...prev, page, pageSize }));
                        }}
                        renderEmpty={() => (
                            <Stack width="100%" minHeight={400} alignItems="center" justifyContent="center">
                                <Typography variant="h2" fontWeight={400} color="primary.dark">
                                    {'No results found'}
                                </Typography>
                                <Typography variant="h6" fontWeight={400} align="center">
                                    <>
                                        We couldn't find what searched for. <br />
                                        Try searching again.
                                    </>
                                </Typography>
                            </Stack>
                        )}
                    />
                </Box>
                {focusedItem?.BlobName && (
                    <Dialog
                        open={!!focusedItem.BlobName}
                        onClose={() => setFocusedItem(undefined)}
                        PaperProps={{
                            sx: {
                                minWidth: 1280,
                                minHeight: 720,
                            },
                        }}
                    >
                        <TransformWrapperComponent
                            handleTransform={handleTransform}
                            loading={loading}
                            displayWidth={1280}
                            displayHeight={720}
                            scale={scaleFull}
                            scaleZoomChangeCursor={scaleZoomChangeCursor}
                            image={image}
                            prevGeoZones={
                                !_.isNil(camera.VendorCameraId) ||
                                !_.isNil(camera.SP111CameraId) ||
                                !_.isNil(camera.PMC8266CameraId)
                                    ? geoZones
                                    : geoZonesCameraView
                            }
                            cameraPhotoVehicles={focusedItem?.CameraPhotoVehicles || []}
                        />
                    </Dialog>
                )}
            </Box>
        </div>
    );
}
